import { OuInfoProps } from '@/api/ou';
import {
  apiV2UetThermalPowerStationCheckUniquePost,
  apiV2UetThermalPowerStationCreatePost,
  apiV2UetThermalPowerStationUpdatePost,
} from '@maxtropy/device-customer-apis-v2';
import { Form, Input, Modal, Select } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ThermalPowerStationItem } from '../types';

interface Props {
  open: boolean;
  station?: ThermalPowerStationItem;
  ouList?: OuInfoProps[];
  onCancel: () => void;
  onSuccess: () => void;
}

interface StationFormValues {
  ouId: number;
  name: string;
}

const StationModal: FC<Props> = ({ open, station, ouList, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();

  const title = useMemo(() => (station ? '编辑火电站' : '新建火电站'), [station]);
  const { runAsync, loading } = useRequest(
    async (values: StationFormValues) => {
      const uniqueRes = await apiV2UetThermalPowerStationCheckUniquePost({ name: values.name, id: station?.id });
      if (!uniqueRes.flag) {
        form.setFields([{ name: 'name', errors: ['火电站名称已存在'] }]);
        return false;
      }

      await (station
        ? apiV2UetThermalPowerStationUpdatePost({ id: station.id, ...values })
        : apiV2UetThermalPowerStationCreatePost({ uetId: Number(id), ...values }));
      return true;
    },
    { manual: true }
  );

  useEffect(() => {
    if (open && station) {
      form.setFieldsValue({ name: station.name, ouId: station.ou?.id });
    }
  }, [form, open, station]);

  const handleOk = () => {
    form.validateFields().then((values: StationFormValues) => {
      runAsync(values).then(success => {
        if (!success) return;
        form.resetFields();
        onSuccess();
      });
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={title}
      open={open}
      destroyOnClose
      contentClassName="modal-form-content"
      confirmLoading={loading}
      onCancel={handleCancel}
      onOk={handleOk}
    >
      <Form form={form}>
        <Form.Item label="运营单元" name="ouId" rules={[{ required: true, message: '请选择运营单元' }]}>
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="请选择"
            options={(ouList ?? []).map(item => ({ label: item.name, value: item.id }))}
          />
        </Form.Item>
        <Form.Item
          label="火电站名称"
          name="name"
          rules={[
            { required: true, whitespace: true, message: '请输入火电站名称' },
            { max: 50, message: '最多输入50个字符' },
          ]}
        >
          <Input allowClear placeholder="请输入" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StationModal;
